import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { handleConfirmCancel } from '@src/@core/components/ConfirmAlerts';
import { handleResponseErrors } from '@helpers/Actions';
import CrudApi from '@crudApi';
import Notify from '@src/@core/components/Notify/ActionNotify';
import {
  handleQueuedBulkDeleteResponse,
  isQueuedBulkDeleteResponse,
  notifyDeleteResult
} from '@components/SharedComponents/Realtime';

export const getData = createAsyncThunk('systemTypes/getData', async (params) => {
  const { itemType } = params;
  if (!itemType) {
    throw new Error('Error Invalid type');
  }
  const response = await CrudApi({ apiParams: params, withAdditionalParams: true }).get(`/api/system-configs/${itemType}`);
  return {
    params,
    data: response.data.data.list.data,
    total: response.data.data.list.total
  };
});

export const addSystemType = createAsyncThunk(
  'systemTypes/addSystemType',
  async (params, { dispatch, getState, fulfillWithValue, rejectWithValue }) => {
    const { itemType, items } = params;
    const data = items?.map((item) => ({ ...item, status: 1 }));
    const response = await CrudApi()
      .post(`/api/system-configs/${itemType}`, {
        items: data
      })
      .then((res) => res)
      .catch((e) => handleResponseErrors(e));
    await dispatch(getData(getState().systemTypes.params));
    return response?.status === 200
      ? fulfillWithValue(response)
      : rejectWithValue({ response });
  }
);

export const updateSystemType = createAsyncThunk(
  'systemTypes/editSystemType',
  async (params, { dispatch, getState, fulfillWithValue, rejectWithValue }) => {
    const { itemType, id, ...restParams } = params;
    const response = await CrudApi()
      .post(`/api/system-configs/${itemType}/${id}`, {
        ...restParams
      })
      .then((res) => res)
      .catch((e) => handleResponseErrors(e));
    await dispatch(getData(getState().systemTypes.params));
    return response?.status === 200
      ? fulfillWithValue(response)
      : rejectWithValue({ response });
  }
);

export const updateStatus = createAsyncThunk(
  'systemTypes/updateStatus',
  async (params, { dispatch, getState, fulfillWithValue, rejectWithValue }) => {
    const { itemType, id, status } = params;
    const response = await CrudApi({ useFormData: false, useFilteredBranches: false })
      .post(`/api/system-configs/${itemType}/${id}`, {
        status: status
      })
      .then((res) => res)
      .catch((e) => handleResponseErrors(e));
    await dispatch(getData(getState().systemTypes.params));
    return response?.status === 200
      ? fulfillWithValue(response)
      : rejectWithValue({ response });
  }
);

// A queued delete only finishes over realtime, so this list refetches itself there —
// same shape as the queued import (see `getDropdownImportConfig` in ../Helpers).
const refreshList = (dispatch, getState) => () => {
  dispatch(setDeleting(false));
  dispatch(getData(getState().systemTypes.params));
};

export const deleteSystemType = createAsyncThunk(
  'systemTypes/deleteSystemType',
  async (params, { dispatch, getState, fulfillWithValue, rejectWithValue }) => {
    const { itemType, id } = params;
    const response = await CrudApi({ useFormData: false, useFilteredBranches: false })
      .delete(`/api/system-configs/${itemType}`, {
        data: {
          ids: [id]
        }
      })
      .then((res) => res)
      .catch((e) => handleResponseErrors(e));
    const queued = handleQueuedBulkDeleteResponse(response, { itemType, onComplete: refreshList(dispatch, getState) });
    if (!queued) await dispatch(getData(getState().systemTypes.params));
    return response?.status === 200
      ? fulfillWithValue(response)
      : rejectWithValue({ response });
  }
);

export const handleDelete = createAsyncThunk(
  'systemTypes/handleDelete',
  async (params, { dispatch }) => {
    handleConfirmCancel({
      title: 'Delete',
      text: 'This item will be deleted, kindly confirm?',
      type: 'error',
      confirmCallback: () => {
        dispatch(deleteSystemType(params));
      },
      buttons: { confirm: 'Delete', cancel: 'Cancel' },
      options: { confirmClassNames: 'btn-danger' }
    });
  }
);

export const deleteMultiSystemType = createAsyncThunk(
  'systemTypes/deleteMultiSystemType',
  async (params, { dispatch, getState, fulfillWithValue, rejectWithValue }) => {
    const { itemType, ids } = params;
    const response = await CrudApi({ useFormData: false, useFilteredBranches: false })
      .delete(`/api/system-configs/${itemType}`, {
        data: {
          ids: ids
        }
      })
      .then((res) => res)
      .catch((e) => handleResponseErrors(e));
    const queued = handleQueuedBulkDeleteResponse(response, { itemType, onComplete: refreshList(dispatch, getState) });
    if (!queued) await dispatch(getData(getState().systemTypes.params));
    return response?.status === 200
      ? fulfillWithValue(response)
      : rejectWithValue({ response });
  }
);

export const AddBranchesTeam = createAsyncThunk(
  'systemTypes/addBranchesTeam',
  async (params, { dispatch, getState, fulfillWithValue, rejectWithValue }) => {
    const { itemType, id, ...restParams } = params;
    const response = await CrudApi()
      .post(`/api/system-configs/branches-teams`, {
        ...restParams
      })
      .then((res) => res)
      .catch((e) => handleResponseErrors(e));
    await dispatch(getData(getState().systemTypes.params));
    return response?.status === 200
      ? fulfillWithValue(response)
      : rejectWithValue({ response });
  }
);

export const systemTypesSlice = createSlice({
  name: 'systemTypes',
  initialState: {
    isReady: false,
    data: [],
    total: 1,
    params: {},
    allData: [],
    systemType: {},
    isDeleting: false
  },
  reducers: {
    resetSystemType: (state, action) => {
      return {
        isReady: false,
        data: [],
        total: 1,
        params: {},
        allData: [],
        systemType: {},
        isDeleting: false
      };
    },
    setDeleting: (state, action) => {
      state.isDeleting = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getData.fulfilled, (state, action) => {
        state.data = action.payload.data;
        state.params = action.payload.params;
        state.total = action.payload.total;
      })
      .addCase(getData.rejected, (state, action) => {
        state.data = [];
        state.total = 1;
      })
      // A queued delete keeps the lock until `refreshList` lifts it; a direct one lifts it here.
      .addCase(deleteSystemType.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteSystemType.fulfilled, (state, action) => {
        if (!isQueuedBulkDeleteResponse(action.payload)) {
          state.isDeleting = false;
          notifyDeleteResult(action.payload);
        }
      })
      .addCase(deleteSystemType.rejected, (state, action) => {
        state.isDeleting = false;
        Notify.Error({ res: action.payload });
      })
      .addCase(deleteMultiSystemType.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteMultiSystemType.fulfilled, (state, action) => {
        if (!isQueuedBulkDeleteResponse(action.payload)) {
          state.isDeleting = false;
          notifyDeleteResult(action.payload);
        }
      })
      .addCase(deleteMultiSystemType.rejected, (state, action) => {
        state.isDeleting = false;
        Notify.Error({ res: action.payload });
      })
      .addCase(updateSystemType.fulfilled, (state, action) => {
        Notify.Success({ res: action.payload });
      })
      .addCase(updateSystemType.rejected, (state, action) => {
        Notify.Error({ res: action.payload });
      })
      .addCase(addSystemType.fulfilled, (state, action) => {
        Notify.Success({ res: action.payload });
      })
      .addCase(addSystemType.rejected, (state, action) => {
        Notify.Error({ res: action.payload });
      })
      .addCase(updateStatus.fulfilled, (state, action) => {
        Notify.Success({ res: action.payload });
      })
      .addCase(updateStatus.rejected, (state, action) => {
        Notify.Warning({ res: action.payload });
      });
  }
});
export const { resetSystemType, setDeleting } = systemTypesSlice.actions;
export default systemTypesSlice.reducer;
